import React, { useEffect, useState, useRef } from 'react';
import { Upload, Image } from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../utils/api';

const IMG_BASE = process.env.REACT_APP_IMG_URL || 'http://localhost:5000';

const GROUP_LABELS = {
  general: { label: 'Të Përgjithshme',     icon: '⚙️' },
  hero:    { label: 'Hero / Ballina',       icon: '🖼️' },
  contact: { label: 'Kontaktet & Oraret',   icon: '📞' },
  stats:   { label: 'Statistikat',          icon: '📊' },
  social:  { label: 'Rrjetet Sociale',      icon: '🌐' },
};

// Image setting field with upload button
function ImageSettingField({ settingKey, label, currentValue, onFileSelect, fileSelections }) {
  const inputRef = useRef();
  const selectedFile = fileSelections[settingKey];
  const preview = selectedFile
    ? URL.createObjectURL(selectedFile)
    : (currentValue ? `${IMG_BASE}${currentValue}` : null);

  return (
    <div className="form-group image-setting-field" style={{margin:0}}>
      <label className="form-label">{label}</label>
      <div style={{display:'flex',alignItems:'center',gap:10}}>
        {preview
          ? <img src={preview} alt={label} style={{height:44,width:'auto',maxWidth:120,objectFit:'contain',border:'1px solid #eae9e4',borderRadius:6,background:'var(--gray-50)'}}/>
          : <div style={{width:44,height:44,background:'var(--gray-100)',borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center'}}>
              <Image size={20} color="var(--gray-400)"/>
            </div>
        }
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => inputRef.current.click()}>
          <Upload size={14}/> {selectedFile ? 'Ndrysho' : 'Ngarko foto'}
        </button>
        {selectedFile && <span style={{fontSize:'0.75rem',color:'var(--green-600)'}}>✓ {selectedFile.name}</span>}
      </div>
      <input
        ref={inputRef} type="file" accept="image/*" style={{display:'none'}}
        onChange={e => onFileSelect(settingKey, e.target.files[0])}
      />
    </div>
  );
}

export default function AdminSettings() {
  const [settings,       setSettings]       = useState([]);
  const [values,         setValues]         = useState({});
  const [fileSelections, setFileSelections] = useState({});
  const [loading,        setLoading]        = useState(true);
  const [saving,         setSaving]         = useState(false);

  useEffect(() => {
    api.get('/admin/settings').then(r => {
      setSettings(r.data);
      const vals = {};
      r.data.forEach(s => { vals[s.setting_key] = s.value || ''; });
      setValues(vals);
    }).finally(() => setLoading(false));
  }, []);

  const handleFileSelect = (key, file) => {
    if (!file) return;
    setFileSelections(p => ({ ...p, [key]: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      // Append all text/number values
      Object.entries(values).forEach(([k, v]) => {
        const s = settings.find(x => x.setting_key === k);
        if (s && s.type !== 'image') fd.append(k, v || '');
      });
      // Append image files
      Object.entries(fileSelections).forEach(([k, f]) => {
        if (f) fd.append(k, f);
      });
      await api.put('/admin/settings', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Cilësimet u ruajtën me sukses!');
      setFileSelections({});
      // Reload to get fresh image paths
      const r = await api.get('/admin/settings');
      const vals = {};
      r.data.forEach(s => { vals[s.setting_key] = s.value || ''; });
      setValues(vals);
    } catch {
      toast.error('Gabim gjatë ruajtjes.');
    } finally { setSaving(false); }
  };

  // Group settings
  const groups = {};
  settings.forEach(s => {
    if (!groups[s.group_name]) groups[s.group_name] = [];
    groups[s.group_name].push(s);
  });

  if (loading) return <AdminLayout><div className="spinner"/></AdminLayout>;

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="page-title">Cilësimet e Faqes</h1>
        <button form="settings-form" type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Duke ruajtur...' : '💾 Ruaj të gjitha'}
        </button>
      </div>

      <form id="settings-form" onSubmit={handleSubmit}>
        {Object.entries(groups).map(([group, items]) => {
          const meta = GROUP_LABELS[group] || { label: group, icon: '•' };
          return (
            <div key={group} className="card settings-group">
              <div className="settings-group-header">
                <span className="settings-group-icon">{meta.icon}</span>
                <h2 className="settings-group-title">{meta.label}</h2>
              </div>
              <div className="settings-group-body">
                {items.map(s => (
                  <div key={s.setting_key} className="form-group" style={{margin:0}}>
                    {s.type === 'image' ? (
                      <ImageSettingField
                        settingKey={s.setting_key}
                        label={s.label}
                        currentValue={values[s.setting_key]}
                        onFileSelect={handleFileSelect}
                        fileSelections={fileSelections}
                      />
                    ) : s.type === 'textarea' ? (
                      <>
                        <label className="form-label">{s.label}</label>
                        <textarea className="form-input" rows={3}
                          value={values[s.setting_key] || ''}
                          onChange={e => setValues(p => ({ ...p, [s.setting_key]: e.target.value }))}/>
                      </>
                    ) : (
                      <>
                        <label className="form-label">{s.label}</label>
                        <input
                          className="form-input"
                          type={s.type === 'email' ? 'email' : s.type === 'url' ? 'url' : s.type === 'number' ? 'number' : s.type === 'phone' ? 'tel' : 'text'}
                          value={values[s.setting_key] || ''}
                          onChange={e => setValues(p => ({ ...p, [s.setting_key]: e.target.value }))}
                          placeholder={s.type === 'url' ? 'https://' : ''}
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        <div style={{marginTop:'1.5rem',display:'flex',justifyContent:'flex-end'}}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Duke ruajtur...' : '💾 Ruaj cilësimet'}
          </button>
        </div>
      </form>

      <style>{`
        .settings-group { margin-bottom: 1.25rem; overflow: hidden; }
        .settings-group-header {
          display: flex; align-items: center; gap: 10px;
          padding: 1rem 1.5rem; background: var(--gray-50);
          border-bottom: 1px solid #eae9e4;
        }
        .settings-group-icon { font-size: 1.1rem; }
        .settings-group-title { font-size: 0.9rem; font-weight: 700; color: var(--gray-700); }
        .settings-group-body {
          padding: 1.5rem;
          display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem;
        }
        @media(max-width:640px){
          .settings-group-body { grid-template-columns: 1fr; }
        }
      `}</style>
    </AdminLayout>
  );
}
